import { useSearchParams } from 'react-router'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DepositsTab } from '@/components/protection/deposits-tab'
import { SettingsTab } from '@/components/protection/settings-tab'
import { StripeLogsPage } from '@/pages/stripe-logs'

const TABS = ['deposits', 'settings', 'activity'] as const
type Tab = (typeof TABS)[number]

function isTab(value: string | null): value is Tab {
  return TABS.includes(value as Tab)
}

/**
 * What protects each booking: the security deposit on every stay except
 * Airbnb, how deposits are set up, and every Stripe action behind them (CAP-651).
 *
 * Modeled on Uplisting's Protection page, which this replaces now that
 * deposits run here instead of in Uplisting's paid feature. The title lives in
 * the top bar only (workspace UX rule), so the page starts at its tabs. The
 * tab is in the URL, so `/stripe-logs` can land on Activity and a link to
 * Settings survives a reload.
 */
export function ProtectionPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const tab: Tab = isTab(requested) ? requested : 'deposits'

  const selectTab = (next: string) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'deposits') params.delete('tab')
    else params.set('tab', next)
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-textMuted">
        What protects each booking: the security deposit held on every stay except Airbnb.
      </p>
      <Tabs value={tab} onValueChange={selectTab}>
        <TabsList>
          <TabsTrigger value="deposits">Deposits</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>
        {/* Only the open tab mounts, so each tab's queries run when it is looked at. */}
        <TabsContent value="deposits" className="mt-6">
          {tab === 'deposits' && (
            <DepositsTab
              initialWindow={searchParams.get('window') === 'past' ? 'past' : 'upcoming'}
              initialBookingId={searchParams.get('booking') ?? undefined}
            />
          )}
        </TabsContent>
        <TabsContent value="settings" className="mt-6">
          {tab === 'settings' && <SettingsTab />}
        </TabsContent>
        <TabsContent value="activity" className="mt-6">
          {tab === 'activity' && <StripeLogsPage />}
        </TabsContent>
      </Tabs>
    </div>
  )
}
